import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AccountGroupService, ProjectionFilter } from '@galaxy/account-group';
import { BillingService, ProductPricing } from '@galaxy/billing';
import { ItemWhitelabelService } from '@galaxy/inventory-ui';
import { AppPrices, AppPricingService } from '@galaxy/marketplace-apps';
import { EditionsService, MarketplaceAppService as MarketplaceAppsService } from '@galaxy/marketplace-apps/v1';
import { WhitelabelService } from '@galaxy/partner';
import { TranslateService } from '@ngx-translate/core';
import { ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface } from '@vendasta/accounts/legacy';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { PartnerAppEnablementStatusService } from '@vendasta/marketplace-packages';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { ResellerItem } from '@vendasta/store';
import { BehaviorSubject, combineLatest, EMPTY, merge, Observable, Subject } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  pluck,
  scan,
  shareReplay,
  skipWhile,
  startWith,
  switchMap,
} from 'rxjs/operators';
import { ActivationServiceInterface } from '../../common/app-selection/paginated-app-activation-table/activation-service-interface';
import { AccountService, Activation } from '../../core/account.service';
import { PackageService } from '../../core/package.service';
import { ResellerItemService } from '../../core/reseller/reseller-item.service';
import { UniqueIdToItemIdAndQuantityMap } from '../order-form-page/interface';
import { ActivationParamService } from './activation-param.service';
import { ActivationPricingService } from './activation-pricing.service';
import { AppEditionService } from './app-edition.service';
import {
  ActivatableItem,
  ActivatableItemCount,
  ActivatablePackage,
  OrderFormType,
  ProductBoxItems,
  ProductEditionMap,
  QuantityEditionIdPair,
  SelectedItem,
  SelectedItemQueryParams,
  SelectedItems,
} from './interface';
import { PendingActivationDisplay, PendingActivationsService } from './pending-activations.service';
import {
  addActivationToItems,
  assembleActivatablePackages,
  assembleActiveProducts,
  assembleAppCartItems,
  ClearEvent,
  convertExtractedQueryParamsToSelectedItems,
  convertResellerItemsToFlatActivatableItems,
  CurrentSelection,
  doItemsRequireOrderForm,
  extractIdsOfItemsUsingEditions,
  generateSelectedItemQueryParameters,
  mergeActivationsIntoProducts,
  nestActivatableItems,
  updateSelectedItems,
} from './utils';

@Injectable()
export class ActivationService implements ActivationServiceInterface {
  readonly activatableItems$: Observable<ActivatableItem[]>;
  readonly activatableResellerItems$: Observable<ResellerItem[]>;
  readonly pricingMap$: Observable<Map<string, ProductPricing>>;
  readonly appPriceMap$: Observable<Map<string, AppPrices>>;
  readonly accountGroupCountry$: Observable<string>;
  readonly accountGroupId$: Observable<string>;
  readonly partnerId$: Observable<string>;
  readonly activeProducts$: Observable<ProductBoxItems[]>;
  readonly enabledPartnerFeatures$: Observable<string[]>;
  readonly selectedItems$: Observable<SelectedItems>;
  readonly selectedItems$$ = new BehaviorSubject<SelectedItems>(new Map<string, QuantityEditionIdPair>());
  readonly appCartItems$: Observable<ActivatableItemCount[]>;
  readonly activatablePackages$: Observable<ActivatablePackage[]>;
  readonly unfilteredFlattenedActivatableItems$: Observable<ActivatableItem[]>;
  readonly selectedItemsRequireOrderForm$: Observable<OrderFormType>;
  readonly appStatuses$: Observable<ActivationStatusesInterface[]>;
  readonly accountGroupName$: Observable<string>;
  readonly accountGroupMarketId$: Observable<string>;
  readonly activations$: Observable<Activation[]>;
  readonly selectedQueryParams$: Observable<SelectedItemQueryParams>;
  private readonly pendingActivations$: Observable<PendingActivationDisplay[]>;
  readonly missingSelectedActivatableItem$: Observable<boolean>;

  readonly editionsMap$: Observable<ProductEditionMap>;

  private readonly itemSelectedEvents$$: Subject<SelectedItem[]> = new Subject<SelectedItem[]>();
  private readonly itemsAndQuantity$: Observable<UniqueIdToItemIdAndQuantityMap>;
  private readonly clearSelectionEvent$$: Subject<void> = new Subject<void>();
  private readonly loadAppStatuses$$: Subject<void> = new Subject<void>();
  private readonly navigationSource$: Observable<string> = this.paramService.navigationSource$;

  constructor(
    private resellerItemService: ResellerItemService,
    private activationPricingService: ActivationPricingService,
    private paramService: ActivationParamService,
    private whitelabelService: WhitelabelService,
    private accountService: AccountService,
    private packageService: PackageService,
    private accountGroupService: AccountGroupService,
    private editionService: AppEditionService,
    private pendingActivationsService: PendingActivationsService,
    private dialog: MatDialog,
    private billingService: BillingService,
    private marketplaceAppsService: MarketplaceAppsService,
    private editionsService: EditionsService,
    private appPricingService: AppPricingService,
    private enablementService: PartnerAppEnablementStatusService,
    private router: Router,
    private snackbarService: SnackbarService,
    private translateService: TranslateService,
    private itemWhitelabelService: ItemWhitelabelService,
    private analyticsService: ProductAnalyticsService,
  ) {
    this.activations$ = this.accountService.activations$;
    this.itemsAndQuantity$ = this.paramService.resellerItemQuantityMapFromUrl$;
    this.activatableResellerItems$ = this.resellerItemService
      .getActivatablePartnerDataWithoutSuspended()
      .pipe(distinctUntilChanged(), shareReplay(1));
    this.accountGroupId$ = this.paramService.accountGroupId$;
    this.partnerId$ = this.paramService.partnerId$;
    this.editionsMap$ = this.activatableResellerItems$.pipe(
      switchMap((ris) => {
        const ids = extractIdsOfItemsUsingEditions(ris);
        return this.editionService.getEditionsForApps(ids);
      }),
      shareReplay(1),
    );

    const partnerConfiguration$ = this.paramService.partnerId$.pipe(
      switchMap((partnerId) => this.whitelabelService.getConfiguration(partnerId, null)),
      shareReplay(1),
    );
    this.enabledPartnerFeatures$ = partnerConfiguration$.pipe(pluck('enabledFeatures'));

    this.pricingMap$ = this.activationPricingService.productPricingMap$;

    this.appPriceMap$ = this.activationPricingService.appPriceMap$;

    this.appStatuses$ = combineLatest([this.loadAppStatuses$$.pipe(startWith(undefined)), this.accountGroupId$]).pipe(
      switchMap(([_, accountId]) => this.accountService.getAppStatusesForBusiness(accountId)),
      shareReplay(1),
    );

    this.pendingActivations$ = this.pendingActivationsService.getPendingActivations$();

    const accountGroup$ = this.accountGroupId$.pipe(
      filter((a) => !!a),
      switchMap((agid) =>
        this.accountGroupService.get(
          agid,
          new ProjectionFilter({
            napData: true,
            accountGroupExternalIdentifiers: true,
          }),
        ),
      ),
      shareReplay(1),
    );
    const accountGroupNapData$ = accountGroup$.pipe(map((acc) => (acc ? acc.napData : undefined)));
    const accountGroupExternalIds$ = accountGroup$.pipe(map((acc) => (acc ? acc.externalIdentifiers : undefined)));

    this.accountGroupCountry$ = accountGroupNapData$.pipe(
      filter((nap) => !!nap),
      map((nap) => nap.country),
    );
    this.accountGroupName$ = accountGroupNapData$.pipe(
      filter((nap) => !!nap),
      map((nap) => nap.companyName),
    );
    this.accountGroupMarketId$ = accountGroupExternalIds$.pipe(
      filter((ids) => !!ids),
      map((ids) => ids.marketId || 'default'),
    );

    this.unfilteredFlattenedActivatableItems$ = combineLatest([
      this.activatableResellerItems$,
      this.pricingMap$,
      this.accountGroupCountry$,
      this.enabledPartnerFeatures$,
      this.appStatuses$,
      this.accountService.activations$.pipe(startWith([])),
      this.editionsMap$,
      this.pendingActivations$,
      this.appPriceMap$,
      this.activationPricingService.partnerCurrency$,
    ]).pipe(
      map(
        ([
          activatableResellerItems,
          pricingMap,
          country,
          enabledPartnerFeatures,
          appStatuses,
          activations,
          editionsMap,
          pendingActivations,
          appPriceMap,
          partnerCurrency,
        ]: [
          ResellerItem[],
          Map<string, ProductPricing>,
          string,
          string[],
          ActivationStatusesInterface[],
          Activation[],
          ProductEditionMap,
          PendingActivationDisplay[],
          Map<string, AppPrices>,
          string,
        ]) => {
          const items = convertResellerItemsToFlatActivatableItems(
            activatableResellerItems,
            pricingMap,
            country,
            enabledPartnerFeatures,
            appStatuses,
            editionsMap,
            pendingActivations,
            appPriceMap,
            partnerCurrency,
          );
          return addActivationToItems(items, activations);
        },
      ),
      shareReplay(1),
    );

    this.activatableItems$ = this.unfilteredFlattenedActivatableItems$.pipe(
      map((items) => nestActivatableItems(items)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );
    /*
     * Compute the selected items as a result from the url param selections, and further selections through user interaction
     */
    const startSelectedItems$ = this.itemsAndQuantity$.pipe(
      map((itemQuantityMap) => convertExtractedQueryParamsToSelectedItems(itemQuantityMap)),
    );
    const clearEvents$ = this.clearSelectionEvent$$.pipe(map(() => [{ clear: true }]));
    const selectionEvents$ = merge(startSelectedItems$, this.itemSelectedEvents$$);
    this.selectedItems$ = this.selectedItems$$.asObservable();

    const allSelections$ = merge(selectionEvents$, clearEvents$);
    const updatedSelectedItems$ = combineLatest([allSelections$, this.activatableItems$, this.appStatuses$]).pipe(
      map(
        ([events, items, status]: [
          SelectedItem[] | ClearEvent[],
          ActivatableItem[],
          ActivationStatusesInterface[],
        ]) => {
          // Convert an array of events into individual events
          return events.map((event) => {
            const currentSelection: CurrentSelection = {
              event: event,
              items: items,
              appStatus: status,
            };
            return currentSelection;
          });
        },
      ),
      scan(
        (selectedItems, selectionEvents) =>
          selectionEvents.reduce((sis, se) => updateSelectedItems(sis, se), selectedItems),
        new Map<string, QuantityEditionIdPair>() as SelectedItems,
      ),
      startWith(new Map<string, QuantityEditionIdPair>()),
      shareReplay(1),
    );

    updatedSelectedItems$.subscribe((selectedItems) => this.selectedItems$$.next(selectedItems));

    /*
     * Done computing selected items
     */

    this.selectedItemsRequireOrderForm$ = combineLatest([
      // Prevent navigating to the next page before selectedItems has been properly set to contain
      // the items specified in the url parameters.
      this.selectedItems$.pipe(skipWhile((items) => items.size === 0)),
      this.unfilteredFlattenedActivatableItems$,
      this.appStatuses$,
    ]).pipe(
      map(
        ([selectedItems, flatActivatableItems, appStatuses]: [
          SelectedItems,
          ActivatableItem[],
          ActivationStatusesInterface[],
        ]) => {
          const selectedItemIds = Array.from(selectedItems.keys());
          return doItemsRequireOrderForm(selectedItemIds, flatActivatableItems, appStatuses);
        },
      ),
      catchError((err) => {
        this.snackbarService.errorSnack(err.message);
        return EMPTY;
      }),
    );

    this.activeProducts$ = combineLatest([
      this.activatableResellerItems$.pipe(startWith([])),
      this.appStatuses$,
      this.accountService.activations$,
      this.editionsMap$,
    ]).pipe(
      map(([items, appStatuses, activations, editionMap]) => {
        const products = assembleActiveProducts(items, appStatuses);
        return mergeActivationsIntoProducts(activations, products, editionMap);
      }),
      shareReplay(1),
    );

    this.missingSelectedActivatableItem$ = combineLatest([
      this.itemSelectedEvents$$.asObservable(),
      this.unfilteredFlattenedActivatableItems$,
    ]).pipe(
      map(([newSelectedItems, activatableItems]) => {
        return newSelectedItems.some((selectedItem) => {
          return !activatableItems.find((item) => item.appId === selectedItem.appId);
        });
      }),
      shareReplay(1),
    );

    this.appCartItems$ = combineLatest([
      this.selectedItems$,
      this.unfilteredFlattenedActivatableItems$,
      this.editionsMap$,
      this.appStatuses$,
      this.activations$.pipe(startWith([])),
    ]).pipe(
      map(([selectedItems, activatableItems, editionsMap, appStatuses, activations]) => {
        const currentTime = new Date();
        return assembleAppCartItems(
          selectedItems,
          activatableItems,
          editionsMap,
          appStatuses,
          activations,
          currentTime,
        );
      }),
      shareReplay(1),
    );

    const packages$ = this.paramService.marketId$.pipe(
      filter((id) => !!id),
      distinctUntilChanged(),
      switchMap((marketId) => this.packageService.getPackagesForMarket$(marketId)),
    );

    this.activatablePackages$ = combineLatest([
      packages$,
      this.unfilteredFlattenedActivatableItems$,
      this.appStatuses$,
      this.activations$.pipe(startWith([])),
    ]).pipe(map((argumentsForAssemblePackages) => assembleActivatablePackages(...argumentsForAssemblePackages)));

    this.selectedQueryParams$ = combineLatest([
      this.unfilteredFlattenedActivatableItems$,
      this.selectedItems$,
      this.partnerId$,
      this.accountGroupId$,
      this.activeProducts$,
      this.paramService.navigationSource$,
    ]).pipe(
      map((argumentsForQueryParams) => generateSelectedItemQueryParameters(...argumentsForQueryParams)),
      shareReplay(1),
    );
  }

  selectItem(selectedItem: SelectedItem): void {
    this.itemSelectedEvents$$.next([selectedItem]);
  }

  selectItems(selectedItems: SelectedItem[]): void {
    this.itemSelectedEvents$$.next(selectedItems);
  }

  clearSelections(): void {
    this.clearSelectionEvent$$.next();
    this.loadAppStatuses$$.next();
  }
}
